import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ApprovalLevelWithDynamic, ApprovalPosition } from '../../../api/types/dasTickets.types';

/**
 * ApprovalFlowDiagram Props
 */
export interface ApprovalFlowDiagramProps {
  approvalLevels: ApprovalLevelWithDynamic[];
  currentUserPosition: ApprovalPosition;
}

/**
 * ApprovalFlowDiagram Component
 *
 * Visual representation of the approval workflow hierarchy
 * Shows all approval levels, current status, and dynamic insertions
 */
const ApprovalFlowDiagram: React.FC<ApprovalFlowDiagramProps> = ({
  approvalLevels,
  currentUserPosition,
}) => {
  // Get status color
  const getStatusColor = (status: 'completed' | 'current' | 'pending') => {
    switch (status) {
      case 'completed':
        return '#10b981'; // green
      case 'current':
        return '#3b82f6'; // blue
      case 'pending':
        return '#94a3b8'; // gray
      default:
        return '#94a3b8';
    }
  };

  // Determine level status
  const getLevelStatus = (level: ApprovalLevelWithDynamic): 'completed' | 'current' | 'pending' => {
    if (level.level < currentUserPosition.level) {
      return 'completed';
    } else if (level.level === currentUserPosition.level) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  // Sort levels: by level number ascending, with Final Approvers always last
  const sortedLevels = [...approvalLevels].sort((a, b) => {
    // Final approvers always go to the end
    if (a.isFinalApprover && !b.isFinalApprover) return 1;
    if (!a.isFinalApprover && b.isFinalApprover) return -1;
    // Then sort by level (ascending)
    return a.level - b.level;
  });

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h3
        style={{
          fontSize: '1.125rem',
          fontWeight: 600,
          marginBottom: '1rem',
          color: '#1e293b',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0.5rem',
        }}
      >
        Approval Flow
      </h3>

      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '0.5rem',
          padding: '1rem',
        }}
      >
        {sortedLevels.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.875rem', padding: '1rem' }}>
            No approval levels available
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            {sortedLevels.map((level, index) => {
              const status = getLevelStatus(level);
              const statusColor = getStatusColor(status);
              const isLast = index === sortedLevels.length - 1;
              const isUserLevel = level.level === currentUserPosition.level;

              return (
                <div key={`level-${level.level}`} style={{ position: 'relative' }}>
                  {/* Connecting Line */}
                  {!isLast && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '19px',
                        top: '40px',
                        width: '2px',
                        height: '40px',
                        background: status === 'completed' ? '#10b981' : '#e5e7eb',
                      }}
                    />
                  )}

                  {/* Level Item */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: isLast ? 0 : '2.5rem',
                      position: 'relative',
                    }}
                  >
                    {/* Status Icon */}
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: statusColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        border: isUserLevel ? '3px solid #fbbf24' : 'none',
                        boxShadow: isUserLevel ? '0 0 0 4px rgba(251, 191, 36, 0.2)' : 'none',
                      }}
                    >
                      {status === 'completed' ? (
                        <IconSystem.check size={20} color="white" />
                      ) : status === 'current' ? (
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: 'white',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: 'white',
                            opacity: 0.5,
                          }}
                        />
                      )}
                    </div>

                    {/* Level Details */}
                    <div style={{ flex: 1, paddingTop: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span
                          style={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: status === 'pending' ? '#94a3b8' : '#1e293b',
                          }}
                        >
                          Level {level.level}: {level.levelName}
                        </span>

                        {level.isDynamicallyInserted && (
                          <span
                            style={{
                              fontSize: '0.625rem',
                              padding: '0.125rem 0.375rem',
                              background: '#fef3c7',
                              color: '#92400e',
                              borderRadius: '0.25rem',
                              fontWeight: 600,
                            }}
                          >
                            DYNAMIC
                          </span>
                        )}

                        {isUserLevel && (
                          <span
                            style={{
                              fontSize: '0.625rem',
                              padding: '0.125rem 0.375rem',
                              background: '#fef3c7',
                              color: '#92400e',
                              borderRadius: '0.25rem',
                              fontWeight: 600,
                            }}
                          >
                            YOU
                          </span>
                        )}
                      </div>

                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        <span>
                          {level.approverName} ({level.approverRole})
                        </span>
                      </div>

                      {level.isDynamicallyInserted && level.insertedByName && (
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: '#92400e',
                            marginTop: '0.25rem',
                            fontStyle: 'italic',
                          }}
                        >
                          Added by {level.insertedByName}
                          {level.insertionReason && ` - ${level.insertionReason}`}
                        </div>
                      )}

                      {status === 'completed' && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            marginTop: '0.25rem',
                            fontSize: '0.75rem',
                            color: '#10b981',
                            fontWeight: 500,
                          }}
                        >
                          <IconSystem.check size={14} />
                          <span>Completed</span>
                        </div>
                      )}

                      {status === 'current' && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            marginTop: '0.25rem',
                            fontSize: '0.75rem',
                            color: '#3b82f6',
                            fontWeight: 500,
                          }}
                        >
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: '#3b82f6',
                              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                            }}
                          />
                          <span>Pending Your Action</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ApprovalFlowDiagram;
